import React, { useState } from 'react';
import { FaTimes, FaCheckCircle } from 'react-icons/fa';
import { WAITLIST_ONLY } from '../config';

/**
 * Waitlist / auth modal.
 * In WAITLIST_ONLY mode: email capture only — never creates a session or opens product.
 */
function LoginOverlay({
  isAuthenticated,
  error,
  loading,
  login,
  joinWaitlist,
  onClose,
  showOverlay = true,
  defaultMode = 'signup',
}) {
  const [email, setEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [joinedEmail, setJoinedEmail] = useState('');

  // Product auth is disabled site-wide — never hide overlay just because of stale isAuthenticated
  if ((!WAITLIST_ONLY && isAuthenticated) || !showOverlay) return null;

  const handleJoinWaitlist = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!ok) {
      setLoginError('Enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setLoginError('');
    try {
      if (typeof joinWaitlist === 'function') {
        await joinWaitlist(trimmed);
      } else if (typeof login === 'function') {
        // Legacy prop — joinWaitlist must be preferred; login must not grant access
        await login('waitlist', trimmed);
      }
      setJoinedEmail(trimmed);
      setJoined(true);
      setEmail('');
    } catch (err) {
      setLoginError(err?.message || 'Could not join waitlist. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && email.trim() && !joined) {
      handleJoinWaitlist();
    }
  };

  return (
    <div className="login-overlay" onClick={onClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <div className="login-content">
          <div className="login-form-centered">
            <img src="/textlogo.png" alt="Broader Business" className="login-text-logo" />

            {joined ? (
              <div className="waitlist-success" role="status">
                <FaCheckCircle className="waitlist-success-icon" aria-hidden />
                <h2 className="waitlist-success-title">You&apos;re on the waitlist</h2>
                <p className="waitlist-success-body">
                  Thanks{joinedEmail ? ` — we saved ${joinedEmail}` : ''}. We&apos;ll email you when
                  Broader Business opens. No account or dashboard access yet.
                </p>
                <button className="login-button email waitlist-button" type="button" onClick={onClose}>
                  <span>Done</span>
                </button>
              </div>
            ) : (
              <>
                <p>Join the waitlist to get early access</p>
                <p className="waitlist-subcopy">
                  No login or dashboard yet — we&apos;ll notify you when it&apos;s ready.
                </p>
                {(error || loginError) && (
                  <p className="error-message">{loginError || error}</p>
                )}
                <div className="email-section centered">
                  <label className="email-label" htmlFor="bb-waitlist-email">
                    Email
                  </label>
                  <input
                    id="bb-waitlist-email"
                    type="email"
                    className="email-input"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading || isSubmitting}
                    autoComplete="email"
                    autoCapitalize="none"
                  />
                  <button
                    className="login-button email waitlist-button"
                    type="button"
                    onClick={handleJoinWaitlist}
                    disabled={loading || isSubmitting || !email.trim()}
                  >
                    <span>
                      {loading || isSubmitting ? 'Joining…' : 'Join Waitlist'}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="login-background">
          <div className="background-logo">
            <img src="/bb.png" alt="" className="bb-logo" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginOverlay;
